/* import __COLOCATED_TEMPLATE__ from './bot-content.hbs'; */
/* RESPONSIBLE TEAM: team-inbox-experience */
import Component from '@glimmer/component';
import type PartGroup from 'embercom/objects/inbox/conversation-stream/part-group';
import type RenderablePart from 'embercom/objects/inbox/renderable-part';
import type Session from 'embercom/services/session';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import type CustomBot from 'embercom/objects/inbox/renderable/custom-bot';
import type BotComment from 'embercom/objects/inbox/renderable/bot-comment';
import type ChannelAndReplyTimeExpectation from 'embercom/objects/inbox/renderable/channel-and-reply-time-expectation';
import type BotNote from 'embercom/objects/inbox/renderable/bot-note';
import type FinAnswer from 'embercom/objects/inbox/renderable/fin-answer';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import {
  translatedPartExists,
  getAllTranslationLanguageCodesFromPartGroup,
  getOriginalLanguageCodeFromPartGroup,
  getLanguageLabelFromCode,
} from '../helpers/translations';
import { RenderableType } from 'embercom/models/data/inbox/renderable-types';
import type ConversationTranslationSettings from 'embercom/services/conversation-translation-settings';
import type Conversation from 'embercom/objects/inbox/conversation';
import {
  nextInCycle,
  normalizeLocale,
  getDefaultWorkspaceLocale,
} from '../helpers/language-selection';
import { computeTranslationUi } from '../helpers/translation-ui';
import { shouldRouteToAutomation } from '../helpers/ai-agent-routing';

interface BotContentRenderablePart extends RenderablePart {
  renderableData: CustomBot | BotComment | ChannelAndReplyTimeExpectation | BotNote | FinAnswer;
}

interface BotContentPartGroup extends PartGroup {
  parts: Array<BotContentRenderablePart>;
}

interface Args {
  isTicketStream: boolean;
  partGroup: BotContentPartGroup;
  isReadOnly: boolean;
  conversation?: Conversation;
  isStandalone?: boolean;
}

interface Signature {
  Args: Args;
}
export default class ConversationStreamTeammateContentComponent extends Component<Signature> {
  @service declare session: Session;
  @service declare intl: IntlService;
  @service declare conversationTranslationSettings: ConversationTranslationSettings;

  get availableTranslationCodes(): string[] {
    return getAllTranslationLanguageCodesFromPartGroup(this.args.partGroup);
  }

  get originalLanguageCode(): string | undefined {
    return getOriginalLanguageCodeFromPartGroup(this.args.partGroup);
  }

  get defaultLanguageCode(): string | undefined {
    return getDefaultWorkspaceLocale(this.session);
  }

  get isFinAnswerPartGroup(): boolean {
    return this.args.partGroup.parts.some(
      (part) => part.renderableType === RenderableType.FinAnswer,
    );
  }

  get conversationLanguage(): string | undefined {
    let conv = this.args.conversation ?? this.args.partGroup.conversation;
    return conv?.getAttributeForDescriptor('language')?.value as string | undefined;
  }

  get canUseFinAnswerTranslationFix(): boolean {
    return this.session.workspace?.canUseFeature('fin-answer-translation-display-fix') ?? false;
  }

  private get shouldRouteAvatarToAutomation(): boolean {
    return shouldRouteToAutomation(this.session.workspace, this.avatarObject);
  }

  private get translationUi() {
    return computeTranslationUi({
      session: this.session,
      available: this.availableTranslationCodes,
      preferred: this.session?.teammate?.teammatePreferredLocale,
      defaultLang: this.defaultLanguageCode,
      original: this.originalLanguageCode,
      selectedLangCode:
        (this.args.conversation &&
          this.conversationTranslationSettings.languageOverrideForConversation(
            this.args.conversation.id,
          )) ||
        this.selectedLangCode,
    });
  }

  get cycleOrderCodes(): string[] {
    return this.translationUi.cycleOrderCodes;
  }

  get currentSelectedCode(): string | undefined {
    return this.translationUi.currentSelectedCode;
  }

  get selectedTranslationLanguageCode(): string | undefined {
    // When auto-translation is disabled, we still want to show the translated
    // content by default (no toggle). Returning undefined lets the downstream
    // Interblocks translator pick the first non-original translation language.
    if (!this.autoTranslationEnabled) {
      return undefined;
    }
    return this.currentSelectedCode;
  }

  get selectedLanguageLabel(): string {
    let code = this.selectedTranslationLanguageCode;
    if (!code) {
      return '';
    }
    return getLanguageLabelFromCode(code, this.intl.primaryLocale) || '';
  }

  get showTranslationQualityFeedback() {
    return this.session.workspace.isFeatureEnabled('translation-quality-feedback');
  }

  get translatedLanguageCode(): string | undefined {
    let code = this.conversationLanguage;
    if (code) {
      return code;
    }
    let original = normalizeLocale(this.originalLanguageCode);
    return (
      this.cycleOrderCodes.find((c) => c !== original) ||
      this.defaultLanguageCode ||
      this.session.workspace?.locale
    );
  }

  get currentTranslationAudience(): 'inbound' | 'outbound' {
    let current = normalizeLocale(this.currentSelectedCode);
    let customerCode = normalizeLocale(this.translatedLanguageCode);
    if (customerCode && current === customerCode) {
      return 'outbound';
    }
    return 'inbound';
  }

  @tracked selectedLangCode?: string;

  get renderableData() {
    return this.args.partGroup.parts[0].renderableData;
  }

  get avatarObject() {
    if (this.customBotSummary) {
      return {
        id: this.customBotSummary.id,
        name: this.intl.t('inbox.custom-bot', { name: this.customBotSummary.title }),
        imageURL: this.renderableData.entity.imageURL,
      };
    } else {
      return this.renderableData.creatingEntity.object;
    }
  }

  get avatarRoute() {
    if (this.customBotSummary) {
      return 'apps.app.operator.custom-bots.custom-bot.show';
    } else if (this.shouldRouteAvatarToAutomation) {
      return 'apps.app.settings.ai-automation.automation';
    } else {
      return 'apps.app.admins.admin';
    }
  }

  get avatarRouteModels() {
    if (this.customBotSummary) {
      return [this.session.workspace.id, this.customBotSummary.id];
    } else if (this.shouldRouteAvatarToAutomation) {
      return [this.session.workspace.id];
    } else {
      return [this.session.workspace.id, this.avatarObject.id];
    }
  }

  get customBotSummary() {
    return 'customBotSummary' in this.renderableData && this.renderableData.customBotSummary;
  }

  get currentTranslatablePart() {
    return this.args.partGroup.parts.find((p) => p.renderableType === 7 || p.renderableType === 58);
  }

  get autoTranslationEnabled() {
    return this.conversationTranslationSettings.autoTranslationEnabledForConversationObject(
      this.args.conversation,
    );
  }

  get showOriginalMessage(): boolean {
    if (!this.translatedPartExists) {
      return true;
    }
    if (this.args.isReadOnly) {
      return (
        normalizeLocale(this.currentSelectedCode) === normalizeLocale(this.originalLanguageCode)
      );
    }
    if (!this.autoTranslationEnabled) {
      return true;
    }
    return normalizeLocale(this.currentSelectedCode) === normalizeLocale(this.originalLanguageCode);
  }

  get showTranslatedMessage() {
    return !this.showOriginalMessage;
  }

  get translatedPartExists() {
    return translatedPartExists(this.args.partGroup);
  }

  get translatedLanguage() {
    let conv = this.args.conversation ?? this.args.partGroup.conversation;
    let code = conv?.getAttributeForDescriptor('language')?.value as string | undefined;
    if (!code) {
      let original = normalizeLocale(this.originalLanguageCode);
      code =
        this.cycleOrderCodes.find((c) => c !== original) ||
        this.defaultLanguageCode ||
        this.session.workspace?.locale;
    }
    return code ? getLanguageLabelFromCode(code, this.intl.primaryLocale) || '' : '';
  }

  @action
  toggleTranslation() {
    this.conversationTranslationSettings.clearLanguageOverride();

    if (this.session.workspace.isAdHocTranslationsEnabled) {
      let normPreferred = normalizeLocale(this.session.teammate?.teammatePreferredLocale);
      let normOriginal = normalizeLocale(this.originalLanguageCode);
      let hasPreferred = this.availableTranslationCodes.some(
        (c) => normalizeLocale(c) === normPreferred,
      );
      let target =
        normPreferred && normOriginal !== normPreferred && hasPreferred
          ? normPreferred
          : this.availableTranslationCodes
              .map((c) => normalizeLocale(c))
              .find((c) => c !== normOriginal);
      if (!target) {
        return;
      }
      this.selectedLangCode =
        normalizeLocale(this.currentSelectedCode) === target ? (normOriginal ?? target) : target;
      return;
    }
    let order = this.cycleOrderCodes;
    let next = nextInCycle(this.currentSelectedCode, order);
    if (next) {
      this.selectedLangCode = next;
    }
  }

  appendPublicSources = (part: BotContentRenderablePart) => {
    return (
      'sources' in part.renderableData &&
      part.renderableData.sources &&
      part.renderableData.sources.length > 0
    );
  };

  partHasTranslations(part: BotContentRenderablePart) {
    let singlePartGroup = { parts: [part] } as unknown as PartGroup;
    return translatedPartExists(singlePartGroup);
  }

  isBotCommentOrFinAnswer(part: RenderablePart): boolean {
    return (
      part.renderableType === RenderableType.BotComment ||
      part.renderableType === RenderableType.FinAnswer
    );
  }
}
declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ConversationStream::PartGroups::BotContent': typeof ConversationStreamTeammateContentComponent;
    'inbox2/conversation-stream/part-groups/bot-content': typeof ConversationStreamTeammateContentComponent;
  }
}
